import { useMemo, useState } from "react";
import type { FullSalonAccessCoreFragment } from "@/gql/graphql";
import type { SalonGroup, SalonWithFranchise } from "./types";
import { getSalonDisplayText, shouldGroupByFranchise } from "./utils";

interface UseSalonSelectorProps {
  availableSalons: SalonWithFranchise[];
  currentSalon: FullSalonAccessCoreFragment | null;
  height?: "default" | "tall" | "compact";
}

export function useSalonSelector({
  availableSalons,
  currentSalon,
  height = "default",
}: UseSalonSelectorProps) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Group salons by franchise
  const salonGroups = useMemo(() => {
    const groupMap = new Map<string, SalonGroup>();

    availableSalons.forEach((salon) => {
      const franchiseId = salon.franchise.id;

      if (!groupMap.has(franchiseId)) {
        groupMap.set(franchiseId, {
          franchise: salon.franchise,
          salons: [],
        });
      }

      groupMap.get(franchiseId)?.salons.push(salon);
    });

    return Array.from(groupMap.values()).sort((a, b) => {
      return a.franchise.name.localeCompare(b.franchise.name);
    });
  }, [availableSalons]);

  // Filter salons based on search term
  const filteredGroups = useMemo(() => {
    if (!searchTerm.trim()) return salonGroups;

    const lowercaseSearch = searchTerm.toLowerCase();

    return salonGroups
      .map((group) => ({
        ...group,
        salons: group.salons.filter(
          (salon) =>
            salon.name.toLowerCase().includes(lowercaseSearch) ||
            salon.city?.toLowerCase().includes(lowercaseSearch) ||
            salon.state?.toLowerCase().includes(lowercaseSearch) ||
            group.franchise?.name.toLowerCase().includes(lowercaseSearch)
        ),
      }))
      .filter((group) => group.salons.length > 0);
  }, [salonGroups, searchTerm]);

  // Height classes based on variant
  const getHeightClasses = () => {
    // Check if current salon display is single line (not multiline)
    const isSingleLine = currentSalon
      ? (() => {
          const group = salonGroups.find((g) => g.salons.some((s) => s.id === currentSalon.id)) || {
            franchise: null,
            salons: [currentSalon],
          };

          const franchiseHasMultipleSalons = group.franchise && group.salons.length > 1;
          const displayData = getSalonDisplayText(currentSalon, franchiseHasMultipleSalons);
          return !displayData.isMultiLine;
        })()
      : true;

    switch (height) {
      case "compact":
        return "min-h-[32px] py-1";
      case "tall":
        return isSingleLine ? "min-h-[44px] py-3" : "min-h-[56px] py-4";
      default:
        return isSingleLine ? "min-h-[36px] py-2" : "min-h-[48px] py-3";
    }
  };

  const handleSalonSelect = (salonId: string, onSalonChange: (salonId: string) => void) => {
    onSalonChange(salonId);
    setOpen(false);
    setSearchTerm("");
  };

  const getCurrentSalonDisplay = () => {
    if (!currentSalon) return null;

    // Find the group for display logic
    const group = salonGroups.find((g) => g.salons.some((s) => s.id === currentSalon.id)) || {
      franchise: null,
      salons: [currentSalon],
    };

    const franchiseHasMultipleSalons = group.franchise && group.salons.length > 1;
    const displayData = getSalonDisplayText(currentSalon, franchiseHasMultipleSalons);

    return displayData;
  };

  const isGroupedByFranchise = shouldGroupByFranchise(filteredGroups);

  return {
    // State
    open,
    setOpen,
    searchTerm,
    setSearchTerm,

    // Computed data
    salonGroups,
    filteredGroups,
    isGroupedByFranchise,

    // Methods
    getHeightClasses,
    handleSalonSelect,
    getCurrentSalonDisplay,
  };
}
